import { type ReactNode } from 'react'
import clsx from 'clsx'
import { motion } from 'motion/react'
import { selectionHaptic, haptic } from '@/lib/tg'

export function StepHeading({ num, title, hint }: { num: number; title: string; hint?: string }) {
  return (
    <div className="mb-3">
      <h2 className="display text-[22px] flex items-baseline gap-2">
        <span className="num" style={{ color: 'var(--color-accent)' }}>{String(num).padStart(2, '0')}</span>
        <span>/ {title}</span>
      </h2>
      {hint && <p className="text-[12px] mono mt-1.5" style={{ color: 'var(--color-muted)' }}>{hint}</p>}
    </div>
  )
}

export interface GridOption<T extends string | number> {
  value: T
  label: string
  prefix?: ReactNode
  suffix?: ReactNode
  disabled?: boolean
}

export function GridSelect<T extends string | number>({
  value, onChange, options, columns = 1,
}: {
  value: T
  onChange: (v: T) => void
  options: GridOption<T>[]
  columns?: 1 | 2 | 3
}) {
  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0,1fr))` }}>
      {options.map(opt => {
        const active = value === opt.value
        return (
          <button
            key={String(opt.value)}
            disabled={opt.disabled}
            onClick={() => { if (opt.disabled) return; selectionHaptic(); onChange(opt.value) }}
            className={clsx(
              'tap relative p-4 rounded-[6px] text-left flex items-center justify-between gap-3 border transition-colors',
              opt.disabled && 'opacity-40 pointer-events-none',
            )}
            style={{
              background: active ? 'var(--color-accent)' : 'var(--color-surface)',
              color: active ? 'var(--color-on-accent)' : 'var(--color-ink)',
              borderColor: active ? 'var(--color-accent)' : 'var(--color-line)',
            }}
          >
            <div className="flex items-center gap-3 min-w-0">
              {opt.prefix}
              <span className="display text-[15px] truncate">{opt.label}</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">{opt.suffix}</div>
          </button>
        )
      })}
    </div>
  )
}

export function Segment<T extends string>({
  value, onChange, options,
}: {
  value: T
  onChange: (v: T) => void
  options: { value: T; label: string }[]
}) {
  return (
    <div className="grid p-1 gap-1 rounded-[6px] border border-[var(--color-line)] bg-[var(--color-surface-2)]"
      style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0,1fr))` }}>
      {options.map(opt => {
        const active = opt.value === value
        return (
          <button
            key={opt.value}
            onClick={() => { selectionHaptic(); onChange(opt.value) }}
            className="tap h-10 rounded-[4px] display text-[12px] transition-colors"
            style={{
              background: active ? 'var(--color-ink)' : 'transparent',
              color: active ? 'var(--color-ink-inv)' : 'var(--color-muted)',
            }}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

export function Switch({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => { haptic('light'); onChange(!checked) }}
      className="tap relative w-[46px] h-[28px] rounded-full transition-colors"
      style={{
        background: checked ? 'var(--color-accent)' : 'var(--color-bg-deep)',
        border: checked ? '1px solid var(--color-accent)' : '1px solid var(--color-line)',
      }}
    >
      <motion.span
        className="absolute top-[3px] size-[20px] rounded-full"
        style={{ background: checked ? '#fff' : 'var(--color-dim)' }}
        animate={{ left: checked ? 21 : 3 }}
        transition={{ type: 'spring', stiffness: 540, damping: 32 }}
      />
    </button>
  )
}

export function Stepper({ value, onChange, min = 1, max = 99 }: {
  value: number; onChange: (v: number) => void; min?: number; max?: number
}) {
  return (
    <div className="flex items-center rounded-[5px] border border-[var(--color-line)] overflow-hidden">
      <button onClick={() => value > min && (haptic('light'), onChange(value - 1))}
        className="tap size-9 display text-[18px] border-r border-[var(--color-line)] disabled:opacity-30" disabled={value <= min}>−</button>
      <div className="num w-9 text-center text-[15px] font-bold">{value}</div>
      <button onClick={() => value < max && (haptic('light'), onChange(value + 1))}
        className="tap size-9 display text-[18px] border-l border-[var(--color-line)] disabled:opacity-30" disabled={value >= max}>+</button>
    </div>
  )
}

export function StickyTotal({
  label, price, hint, onPay, cta = 'оплатить', disabled,
}: {
  label: string
  price: ReactNode
  hint?: string
  onPay: () => void
  cta?: string
  disabled?: boolean
}) {
  return (
    <div className="sticky bottom-0 z-20 bg-[var(--color-bg)] border-t border-[var(--color-line)]"
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 0px)' }}>
      <div className="flex items-stretch gap-3 p-3">
        <div className="flex-1 px-2 py-1">
          <div className="label">{label}</div>
          <div className="num text-[24px] font-bold mt-0.5">{price}</div>
          {hint && <div className="label mt-0.5">{hint}</div>}
        </div>
        <button
          onClick={() => { if (!disabled) { haptic('medium'); onPay() } }}
          disabled={disabled}
          className="tap px-7 rounded-[6px] bg-[var(--color-accent)] text-[var(--color-on-accent)] display text-[16px] disabled:opacity-50"
        >
          {cta.toUpperCase()}
        </button>
      </div>
    </div>
  )
}
